// Market Benchmarks 2.0: Granular Rate & Efficiency Analysis

export interface ServiceBenchmark {
    // Hourly Analysis
    hourlyRateAvg: number;     // e.g., $125/hr
    hourlyRateRange: [number, number];

    // Efficiency Analysis (Typical hours for a standard ticket of this type)
    // In a real app, this would dynamically adjust based on 'Description' NLP
    typicalDurationHours: number;

    // Unit Analysis (for hauling, etc - simplified for now as 'job average')
    fixedJobAvg: number;
}

export const INDUSTRY_BENCHMARKS: Record<string, ServiceBenchmark> = {
    'electrical': {
        hourlyRateAvg: 135,
        hourlyRateRange: [95, 185],
        typicalDurationHours: 4,
        fixedJobAvg: 1450
    },
    'plumbing': {
        hourlyRateAvg: 110,
        hourlyRateRange: [85, 150],
        typicalDurationHours: 3,
        fixedJobAvg: 950
    },
    'roustabout': { // General labor
        hourlyRateAvg: 65,
        hourlyRateRange: [45, 85],
        typicalDurationHours: 8,
        fixedJobAvg: 500
    },
    'pump_repair': {
        hourlyRateAvg: 145,
        hourlyRateRange: [110, 200],
        typicalDurationHours: 6,
        fixedJobAvg: 2800
    },
    'hauling': { // Water/Oil Hauling - Usually Fixed/Unit
        hourlyRateAvg: 105, // If hourly
        hourlyRateRange: [85, 140],
        typicalDurationHours: 2,
        fixedJobAvg: 350 // Per load avg
    },
    'automation': { // IT / Scada
        hourlyRateAvg: 165,
        hourlyRateRange: [125, 250],
        typicalDurationHours: 3,
        fixedJobAvg: 1200
    },
    'general': {
        hourlyRateAvg: 85,
        hourlyRateRange: [50, 120],
        typicalDurationHours: 5,
        fixedJobAvg: 800
    }
};

interface AuditResult {
    type: 'rate' | 'efficiency' | 'cost' | 'clean';
    status: 'warning' | 'critical' | 'good';
    message: string;
    details?: string;
}

export const auditInvoice = (
    serviceType: string,
    billingType: 'hourly' | 'fixed',
    cost: number,
    hours?: number,
    rate?: number
): AuditResult => {
    const type = serviceType?.toLowerCase().replace(/\s+/g, '_') || 'general';
    const benchmark = INDUSTRY_BENCHMARKS[type] || INDUSTRY_BENCHMARKS['general'];

    // 1. Hourly Rate Audit
    if (billingType === 'hourly' && rate && hours) {
        // Check Rate
        const rateDiff = ((rate - benchmark.hourlyRateAvg) / benchmark.hourlyRateAvg) * 100;
        if (rateDiff > 20) {
            return {
                type: 'rate',
                status: 'critical',
                message: `Hourly Rate High: $${rate}/hr`,
                details: `Market Avg is $${benchmark.hourlyRateAvg}/hr. You are paying ${Math.round(rateDiff)}% premium.`
            };
        }

        // Check Efficiency (Hours)
        // Only flag if huge discrepancy (> 100% more than typical) as jobs vary wildly
        if (hours > benchmark.typicalDurationHours * 2.5) {
            return {
                type: 'efficiency',
                status: 'warning',
                message: `High Hours Billed: ${hours} hrs`,
                details: `Typical ${type} jobs take ~${benchmark.typicalDurationHours} hrs. Verify scope of work.`
            };
        }

        return { type: 'clean', status: 'good', message: 'Rates & Hours within market norms.' };
    }

    // 2. Fixed Cost Audit
    if (billingType === 'fixed') {
        const costDiff = ((cost - benchmark.fixedJobAvg) / benchmark.fixedJobAvg) * 100;
        if (costDiff > 25) {
            return {
                type: 'cost',
                status: 'warning',
                message: `Total Cost High: $${cost}`,
                details: `Regional avg for ${type} is ~$${benchmark.fixedJobAvg}.`
            };
        }
    }

    return { type: 'clean', status: 'good', message: 'Invoice looks standard.' };
};
